<template>
  <div class="dashboard-container tagihan-baru">
    <div class="mb-2 page-title">
      <i class="bi bi-receipt"></i> Buat Tagihan
    </div>

    <!-- Pilih turnamen -->
    <div class="ui-field mb-4">
      <label for="pilih-turnamen" class="ui-label">Turnamen</label>
      <select
        id="pilih-turnamen"
        v-model="turnamenId"
        class="form-control"
        @change="muatAtlet"
      >
        <option value="">— pilih turnamen —</option>
        <option v-for="t in turnamenList" :key="t.id" :value="t.id">
          {{ t.name }}
        </option>
      </select>
    </div>

    <div v-if="memuat" class="tagihan-baru__pesan">
      <span class="spinner-border"></span> Memuat data atlet...
    </div>

    <template v-else-if="turnamenId">
      <!-- Rekening belum diisi panitia -->
      <div v-if="!turnamen.bank_account_number" class="tagihan-baru__catatan">
        <i class="bi bi-exclamation-triangle"></i>
        <span>
          Panitia belum mengisi rekening tujuan untuk turnamen ini. Tagihan
          tetap bisa dibuat, tetapi Anda perlu menghubungi bendahara untuk
          mengetahui ke mana pembayaran dikirim.
        </span>
      </div>

      <!-- Tidak ada atlet tersisa -->
      <div v-if="!atlet.length" class="tagihan-baru__kosong">
        <i class="bi bi-check2-circle"></i>
        <p class="tagihan-baru__kosong-judul">Semua atlet sudah tertagih</p>
        <p>
          Tidak ada atlet yang belum masuk tagihan pada turnamen ini. Tambahkan
          atlet baru dari menu Atlet kalau masih ada yang perlu didaftarkan.
        </p>
        <router-link to="/admin/payment" class="button button-ghost">
          <i class="bi bi-list-ul"></i> Lihat daftar tagihan
        </router-link>
      </div>

      <template v-else>
        <!-- Alat bantu -->
        <router-link
          v-if="turnamen.tampilkan_kuota_kelas"
          to="/admin/ketersediaan-kelas"
          class="tagihan-baru__tautan"
        >
          <i class="bi bi-people-fill"></i>
          Lihat keterisian semua kelas
        </router-link>

        <div class="tagihan-baru__alat">
          <div class="ui-field tagihan-baru__cari">
            <input
              v-model="cari"
              type="search"
              class="form-control"
              placeholder="Cari nama atlet..."
              aria-label="Cari atlet"
            />
          </div>

          <button type="button" class="button button-ghost" @click="pilihSemua">
            <i class="bi" :class="semuaTerpilih ? 'bi-square' : 'bi-check2-square'"></i>
            {{ semuaTerpilih ? 'Batalkan semua' : 'Pilih semua' }}
          </button>
        </div>

        <!-- Daftar atlet -->
        <ul class="atlet-daftar">
          <li
            v-for="a in atletTersaring"
            :key="a.id"
            class="atlet"
            :class="{
              'atlet--terpilih': terpilih.includes(a.id),
              'atlet--terkunci': !a.bisa_ditagih,
            }"
          >
            <label class="atlet__label">
              <input
                type="checkbox"
                class="atlet__centang"
                :value="a.id"
                :disabled="!a.bisa_ditagih"
                v-model="terpilih"
              />

              <span class="atlet__isi">
                <span class="atlet__baris">
                  <span class="atlet__nama">{{ a.nama }}</span>
                  <span class="atlet__chip" :class="a.jenis_kelamin === 'Putri' ? 'atlet__chip--pi' : 'atlet__chip--pa'">{{ a.jenis_kelamin }}</span>
                  <span v-if="a.kelas_tanding" class="atlet__chip atlet__chip--kelas">
                    {{ a.kelas_tanding }}<small v-if="a.rentang_berat"> · {{ a.rentang_berat }}</small>
                  </span>
                  <span class="atlet__meta">
                    <span v-if="a.kategori">{{ gantiTingkatUntuk(a.kategori, turnamenId) }}</span>
                    <span v-if="a.kelompok_usia">· {{ a.kelompok_usia }}</span>
                  </span>
                </span>

                <!--
                  Atlet tanpa kategori bertanding tidak punya tarif. Alasannya
                  ditulis di sini supaya pelatih tahu apa yang harus dilengkapi,
                  bukan cuma melihat baris yang tidak bisa diklik.
                -->
                <span v-if="a.kelas_penuh" class="atlet__alasan atlet__alasan--penuh">
                  <i class="bi bi-slash-circle"></i>
                  Kelas <strong>{{ a.kelas }}</strong> sudah penuh
                  ({{ a.terisi }}/{{ a.kuota }}). Atlet ini belum bisa ditagihkan.
                </span>

                <span v-else-if="a.tarif_belum_diatur" class="atlet__alasan">
                  <i class="bi bi-info-circle"></i>
                  Tarif untuk kategori ini belum diatur oleh panitia.
                </span>

                <!-- Peringatan dini: sisa sedikit berarti pelatih perlu segera memutuskan. -->
                <span v-else-if="a.sisa !== null && a.sisa <= 2" class="atlet__alasan atlet__alasan--sisa">
                  <i class="bi bi-exclamation-triangle"></i>
                  Kelas {{ a.kelas }} tinggal {{ a.sisa }} kursi.
                </span>
              </span>

              <span class="atlet__biaya">{{ rupiah(a.biaya) }}</span>
            </label>
          </li>
        </ul>

        <p v-if="!atletTersaring.length" class="tagihan-baru__pesan">
          Tidak ada atlet yang cocok dengan pencarian "{{ cari }}".
        </p>
      </template>
    </template>

    <div v-else class="tagihan-baru__pesan">
      Pilih turnamen lebih dulu untuk melihat atlet yang belum tertagih.
    </div>

    <!--
      Ringkasan menempel di bawah layar.
      Daftar atlet bisa sangat panjang; kalau tombolnya ada di ujung bawah
      halaman, pelatih harus menggulir jauh setiap kali ingin memastikan
      totalnya. Ditempel supaya total dan tombol selalu terlihat.
    -->
    <transition name="naik">
      <div v-if="terpilih.length" class="ringkasan">
        <div class="ringkasan__angka">
          <span class="ringkasan__jumlah">{{ terpilih.length }} atlet dipilih</span>
          <strong class="ringkasan__total">{{ rupiah(total) }}</strong>
        </div>

        <button
          type="button"
          class="button button-primary ringkasan__tombol"
          :disabled="mengirim"
          @click="buatTagihan"
        >
          <i class="bi" :class="mengirim ? 'bi-arrow-repeat berputar' : 'bi-receipt'"></i>
          {{ mengirim ? 'Memproses...' : 'Buat Tagihan' }}
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { gantiTingkatUntuk, muatLabelTingkat } from '@/utils/tingkat';
import { useToast } from 'vue-toastification';
import http from '@/services/http';
import { hanyaTerlihat, pilihTurnamenOtomatis } from '@/utils/status';

export default {
  name: 'BuatTagihanPage',

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      turnamenList: [],
      turnamenId: '',
      turnamen: {},
      atlet: [],
      terpilih: [],
      cari: '',
      memuat: false,
      mengirim: false,
    };
  },

  computed: {
    atletTersaring() {
      const kata = this.cari.trim().toLowerCase();
      if (!kata) return this.atlet;
      return this.atlet.filter((a) => (a.nama || '').toLowerCase().includes(kata));
    },

    /** Hanya atlet yang benar-benar terpilih yang dijumlahkan. */
    total() {
      return this.atlet
        .filter((a) => this.terpilih.includes(a.id))
        .reduce((jml, a) => jml + Number(a.biaya || 0), 0);
    },

    /** Membandingkan terhadap yang SEDANG TAMPIL, bukan seluruh daftar. */
    semuaTerpilih() {
      const bisa = this.atletTersaring.filter((a) => a.bisa_ditagih);
      return bisa.length > 0 && bisa.every((a) => this.terpilih.includes(a.id));
    },
  },

  mounted() {
    muatLabelTingkat();
    this.muatTurnamen();
  },

  methods: {
    gantiTingkatUntuk,
    /*
     * Disimpan saat kotak kehilangan fokus (@change), bukan tiap ketikan.
     * Menyimpan per ketikan akan mengirim "5", "52", "520" saat orang
     * mengetik 52 lalu menghapus — dan nilai antara itu ikut tersimpan.
     */
    rupiah(nilai) {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(Number(nilai || 0));
    },

    async muatTurnamen() {
      try {
        const { data } = await http.get('/tournaments', { params: { fetch_all: true } });
        const daftar = Array.isArray(data) ? data : data.data || [];
        this.turnamenList = hanyaTerlihat(daftar);

        this.turnamenId = pilihTurnamenOtomatis(this.turnamenList, this.$route.query);
        if (this.turnamenId) {
          this.muatAtlet();
        }
      } catch (e) {
        this.toast.error('Gagal memuat daftar turnamen.');
      }
    },

    async muatAtlet() {
      this.terpilih = [];
      this.cari = '';
      if (!this.turnamenId) {
        this.atlet = [];
        return;
      }

      this.memuat = true;
      try {
        const { data } = await http.get('/billings/atlet-belum-tertagih', {
          params: { tournament_id: this.turnamenId },
        });
        this.turnamen = data.turnamen || {};
        this.atlet = data.atlet || [];
      } catch (e) {
        this.atlet = [];
        this.toast.error(e?.response?.data?.message || 'Gagal memuat data atlet.');
      } finally {
        this.memuat = false;
      }
    },

    pilihSemua() {
      const bisa = this.atletTersaring.filter((a) => a.bisa_ditagih).map((a) => a.id);

      if (this.semuaTerpilih) {
        // Hanya yang sedang tampil yang dibatalkan; pilihan pada atlet yang
        // tersembunyi oleh pencarian tidak ikut hilang.
        this.terpilih = this.terpilih.filter((id) => !bisa.includes(id));
      } else {
        this.terpilih = [...new Set([...this.terpilih, ...bisa])];
      }
    },

    async buatTagihan() {
      if (!this.terpilih.length) return;

      this.mengirim = true;
      try {
        const { data } = await http.post('/billings', {
          tournament_id: this.turnamenId,
          member_ids: this.atlet.filter((a) => this.terpilih.includes(a.id)).flatMap((a) => a.member_ids || [a.id]),
        });

        this.toast.success(`Tagihan ${data.invoice_number || ''} berhasil dibuat.`);
        this.$router.push(`/admin/payment/${data.id}`);
      } catch (e) {
        this.toast.error(e?.response?.data?.message || 'Gagal membuat tagihan.');
      } finally {
        this.mengirim = false;
      }
    },
  },
};
</script>

<style scoped>
.tagihan-baru {
  /* Ruang untuk ringkasan yang menempel di bawah. */
  padding-bottom: 120px;
}

.tagihan-baru__alat {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.tagihan-baru__cari { flex: 1; margin: 0; }

@media (min-width: 640px) {
  .tagihan-baru__alat { flex-direction: row; align-items: center; }
  .tagihan-baru__cari { max-width: 340px; }
}

.tagihan-baru__pesan,
.tagihan-baru__kosong {
  padding: var(--space-8) var(--space-4);
  text-align: center;
  color: var(--text-muted);
}

.tagihan-baru__kosong i { font-size: 2rem; color: var(--success-500); }
.tagihan-baru__kosong-judul {
  margin-top: var(--space-3);
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--text-primary);
}

.tagihan-baru__catatan {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4);
  margin-bottom: var(--space-4);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  background: var(--accent-subtle);
  border: 1px solid var(--accent);
  border-radius: var(--radius-lg);
}

.tagihan-baru__catatan i { color: var(--accent); margin-top: 2px; }

/* ── Daftar atlet ── */
.atlet-daftar { list-style: none; margin: 0; padding: 0; display: grid; gap: var(--space-2); }

.atlet {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  transition: border-color var(--transition-fast), background-color var(--transition-fast);
}

.atlet:hover { border-color: var(--border-strong); }
.atlet--terpilih { border-color: var(--accent); background: var(--accent-subtle); }
.atlet--terkunci { opacity: 0.65; }

.atlet__label {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  /* Seluruh badan baris bisa disentuh, bukan cuma kotak centangnya. */
  padding: var(--space-2) var(--space-4);
  margin: 0;
  cursor: pointer;
  min-height: 44px;
}

.atlet--terkunci .atlet__label { cursor: not-allowed; }

.atlet__centang {
  width: 18px; height: 18px;
  margin-top: 0;
  accent-color: var(--accent);
  flex-shrink: 0;
}

.atlet__isi { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }

.atlet__nama {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-primary);
  overflow-wrap: anywhere;
}

.atlet__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.atlet__alasan {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 4px;
  font-size: var(--text-xs);
  color: var(--text-secondary);
}

.atlet__alasan--penuh { color: var(--danger-600); }
.atlet__alasan--sisa  { color: var(--text-brand); }

.tagihan-baru__tautan {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-brand);
  text-decoration: none;
}

.tagihan-baru__tautan:hover { text-decoration: underline; }

.atlet__biaya {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  align-self: center;
}

/* ── Ringkasan menempel ── */
.ringkasan {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1020;
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4);
  padding-bottom: calc(var(--space-4) + var(--safe-bottom));
  background: var(--bg-surface);
  border-top: 1px solid var(--border-subtle);
  box-shadow: 0 -8px 24px -12px rgb(0 0 0 / 0.35);
}

@media (min-width: 992px) {
  /* Bilah sisi admin selebar 280px — ringkasan tidak boleh menutupinya. */
  .ringkasan { left: 280px; }
}

.ringkasan__angka { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.ringkasan__jumlah { font-size: var(--text-xs); color: var(--text-muted); }
.ringkasan__total {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: 800;
  color: var(--text-primary);
}

.ringkasan__tombol { flex-shrink: 0; }

.naik-enter-active, .naik-leave-active { transition: transform 220ms var(--ease-out-quart), opacity 220ms; }
.naik-enter-from, .naik-leave-to { transform: translateY(100%); opacity: 0; }

.berputar { animation: putar 900ms linear infinite; }
@keyframes putar { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .naik-enter-active, .naik-leave-active, .berputar { transition: none; animation: none; }
}

.atlet__baris { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; min-height: 22px; }
.atlet__chip {
  display: inline-flex; align-items: center; gap: 2px;
  font-size: var(--text-xs); font-weight: 600; line-height: 1.4;
  padding: 0 8px; border-radius: 999px;
  border: 1px solid var(--border-subtle); background: var(--bg-base); color: var(--text-secondary);
}
.atlet__chip small { font-weight: 500; color: var(--text-muted); }
.atlet__chip--pa { color: #1d4ed8; background: rgba(37, 99, 235, .1); border-color: rgba(37, 99, 235, .25); }
.atlet__chip--pi { color: #be185d; background: rgba(219, 39, 119, .1); border-color: rgba(219, 39, 119, .25); }
.atlet__chip--kelas { color: var(--text-primary); }
</style>
<style>
[data-theme="dark"] .atlet__chip--pa { color: #93c5fd; }
[data-theme="dark"] .atlet__chip--pi { color: #f9a8d4; }
</style>
